

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Axios from '../utils/axios';
import { IPagination2, ISorting, IQueryParams, IFilters } from '../interfaces/Components/ITable';
import { useSelector } from 'react-redux';
import { Appstore } from '../redux';
import { useLocation } from 'react-router-dom';
import { Option } from '../components/Atomos/DropDown/DDMultiple';
import usePageState from './usePageState';
import { useTablePreferences } from './useTablePreferences';
import { debounce } from "lodash";

interface DataLoaderProps {
  endpoint: string;
  defaultPageSize?: number;
  persistPageSize?: boolean;
  fields?: string[];
  defaultSorting?: ISorting
  persistentFilter?: {[key: string]: IFilters}
  tableKey?: string | null;
}

interface DataLoaderResult<T> {
  data: T[];
  totalCount: number | undefined;
  fetchData: (resetFilters?:boolean, recount?:boolean) => void;
  queryParams?: IQueryParams;
  sort: ISorting;
  setSort:React.Dispatch<React.SetStateAction<ISorting>> 
  pagination: IPagination2;
  setPagination: React.Dispatch<React.SetStateAction<IPagination2>>
  setFilter: React.Dispatch<any>;
  filter: {[key: string]: IFilters}
  inputFilters: any
  setInputFilters: any,
}

const HARD_DEFAULT_PAGE_SIZE = 20;
const MAX_PERSISTED_PAGE_SIZE = 20000;

const cleanObject = (params: any) => {
  if (!params || typeof params !== "object") {
    return {};
  }
  const cleaned: any = {};
  for (const key in params) {
    const f = params[key];
    if (!f) {
      continue;
    }
    if (f.value === "" || f.value === undefined || f.value === null) {
      continue;
    }
    if (Array.isArray(f.value) && f.value.length === 0) {
      continue;
    }
    cleaned[key] = f;
  }
  return cleaned;
};

const buildUrl = (
  endpoint: string,
  filtering: { [key: string]: IFilters } | undefined,
  sorting: ISorting | undefined,
  pagination: IPagination2,
  fields: string[] | undefined,
): string => {
  const params = new URLSearchParams();

  if (fields && fields.length) {
    params.append("fields", JSON.stringify(fields));
  }
  if (filtering && Object.keys(filtering).length) {
    params.append("filter", JSON.stringify(filtering));
  }
  if (sorting && (sorting as any).order !== "default") {
    params.append("sort", JSON.stringify(sorting));
  }
  params.append("pagination", JSON.stringify(pagination));

  const questionIdx = endpoint.indexOf("?");
  const basePath = questionIdx >= 0 ? endpoint.slice(0, questionIdx) : endpoint;
  const merged = new URLSearchParams(
    questionIdx >= 0 ? endpoint.slice(questionIdx + 1) : "",
  );
  params.forEach((value, key) => {
    merged.set(key, value);
  });
  const qs = merged.toString();
  return qs ? `${basePath}?${qs}` : basePath;
};

const useDataLoader = <T extends unknown>({
  endpoint,
  defaultPageSize,
  persistPageSize = true,
  fields,
  persistentFilter,
  defaultSorting,
  tableKey: tableKeyProp,
}: DataLoaderProps): DataLoaderResult<T> => {
  const { pathname } = useLocation();
  const effectiveTableKey =
    tableKeyProp !== undefined && tableKeyProp !== null ? tableKeyProp : pathname;
  const pageState = useSelector((state: Appstore) => state.pageState[pathname]);
  const { tablePrefs, updateTablePrefs } = useTablePreferences(effectiveTableKey);
  const hydratedFromTablePrefs = useRef(false);

  const fallbackPageSize = defaultPageSize ?? HARD_DEFAULT_PAGE_SIZE;

  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [filter, setFilterState] = useState<{ [key: string]: IFilters }>(
    pageState?.filters ?? {},
  );
  const [inputFilters, setInputFiltersState] = useState<{ [key: string]: Option[] }>(
    pageState?.inputFilters ?? {},
  );
  const [sort, setSortState] = useState<ISorting>(
    pageState?.sort ?? defaultSorting ?? { field: "_ts", order: "asc" },
  );
  const [pagination, setPaginationState] = useState<IPagination2>(() => {
    const p = pageState?.pagination as IPagination2 | undefined;
    const candidatePageSize =
      p?.pageSize && p.pageSize > 0 && p.pageSize <= MAX_PERSISTED_PAGE_SIZE
        ? p.pageSize
        : fallbackPageSize;
    return {
      pageIndex: p?.pageIndex ?? 0,
      pageSize: candidatePageSize,
    };
  });

  const stateRef = useRef({ filter, sort, pagination, persistentFilter, fallbackPageSize });
  stateRef.current = { filter, sort, pagination, persistentFilter, fallbackPageSize };

  const fieldsRef = useRef<string[] | undefined>(fields);
  fieldsRef.current = fields;

  const axiosRef = useRef(new Axios());
  const { loadPageState } = usePageState();
  const recountRef = useRef<boolean>(true);
  const requestSeqRef = useRef<number>(0);

  const setFilter = useCallback(
    (action: React.SetStateAction<{ [key: string]: IFilters }>) => {
      setFilterState((prev) => {
        const raw = typeof action === "function" ? action(prev) : action;
        const next = raw != null && !Array.isArray(raw) ? raw : {};
        updateTablePrefs({ filters: next }).catch(() => {});
        return next;
      });
    },
    [updateTablePrefs],
  );

  const setInputFilters = useCallback(
    (action: React.SetStateAction<{ [key: string]: Option[] }>) => {
      setInputFiltersState((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        updateTablePrefs({ inputFilters: next }).catch(() => {});
        return next;
      });
    },
    [updateTablePrefs],
  );

  const setSort = useCallback(
    (action: React.SetStateAction<ISorting>) => {
      setSortState((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        updateTablePrefs({ sorting: next }).catch(() => {});
        return next;
      });
    },
    [updateTablePrefs],
  );

  const setPagination = useCallback(
    (action: React.SetStateAction<IPagination2>) => {
      setPaginationState((prev) => {
        const next = typeof action === "function" ? action(prev) : action;
        const safe: IPagination2 = {
          pageIndex: Math.max(0, next.pageIndex ?? 0),
          pageSize:
            next.pageSize && next.pageSize > 0 ? next.pageSize : fallbackPageSize,
        };
        if (
          persistPageSize &&
          safe.pageSize !== prev.pageSize &&
          safe.pageSize <= MAX_PERSISTED_PAGE_SIZE
        ) {
          updateTablePrefs({ pageSize: safe.pageSize }).catch(() => {});
        }
        return safe;
      });
    },
    [updateTablePrefs, persistPageSize, fallbackPageSize],
  );

  useEffect(() => {
    if (hydratedFromTablePrefs.current) {
      return;
    }
    const hasSortingPref = tablePrefs?.sorting;
    const hasPageSizePref = persistPageSize && tablePrefs?.pageSize !== undefined;
    const hasFiltersPref =
      tablePrefs?.filters != null && Object.keys(tablePrefs.filters).length > 0;
    const hasInputFiltersPref =
      tablePrefs?.inputFilters != null && Object.keys(tablePrefs.inputFilters).length > 0;
    if (!hasSortingPref && !hasPageSizePref && !hasFiltersPref && !hasInputFiltersPref) {
      return;
    }
    hydratedFromTablePrefs.current = true;
    if (tablePrefs.sorting) {
      setSortState(tablePrefs.sorting);
    }
    if (hasPageSizePref && tablePrefs.pageSize !== undefined) {
      const persisted = tablePrefs.pageSize;
      const ps =
        persisted > 0 && persisted <= MAX_PERSISTED_PAGE_SIZE
          ? persisted
          : fallbackPageSize;
      setPaginationState({ pageIndex: 0, pageSize: ps });
    }
    if (hasFiltersPref && tablePrefs.filters) {
      setFilterState(tablePrefs.filters);
    }
    if (hasInputFiltersPref && tablePrefs.inputFilters) {
      setInputFiltersState(tablePrefs.inputFilters as { [key: string]: Option[] });
    }
  }, [tablePrefs, fallbackPageSize, persistPageSize]);

  useEffect(() => {
    hydratedFromTablePrefs.current = false;
  }, [effectiveTableKey]);

  const performFetch = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    try {
      const {
        filter: currentFilter,
        sort: currentSort,
        pagination: currentPagination,
        persistentFilter: currentPersistentFilter,
        fallbackPageSize: fb,
      } = stateRef.current;

      const filtering = cleanObject({
        ...(currentFilter || {}),
        ...(currentPersistentFilter || {}),
      });

      const safePagination: IPagination2 = {
        pageIndex: Math.max(0, currentPagination?.pageIndex ?? 0),
        pageSize:
          currentPagination?.pageSize && currentPagination.pageSize > 0
            ? currentPagination.pageSize
            : fb,
      };

      const dataUrl = buildUrl(
        endpoint,
        filtering,
        currentSort,
        safePagination,
        fieldsRef.current,
      );
      const countUrl = `${dataUrl}&count=true`;

      const shouldCount = recountRef.current;
      recountRef.current = false;

      if (shouldCount) {
        const [dataResponse, countResponse] = await Promise.all([
          axiosRef.current.Get(dataUrl),
          axiosRef.current.Get(countUrl),
        ]);
        if (seq !== requestSeqRef.current) {
          return;
        }
        setData(dataResponse.data);
        setTotalCount(countResponse.data);
      } else {
        const dataResponse = await axiosRef.current.Get(dataUrl);
        if (seq !== requestSeqRef.current) {
          return;
        }
        setData(dataResponse.data);
      }
    } catch (error) {
      console.error("Error al cargar los datos:", error);
    }
  }, [endpoint]);

  const debouncedFetch = useMemo(() => debounce(performFetch, 200), [performFetch]);

  const fetchData = useMemo(() => {
    const fn = (resetFilters?: boolean, recount?: boolean) => {
      if (resetFilters) {
        setFilterState((prev) => (prev && Object.keys(prev).length ? {} : prev));
        setPaginationState((prev) =>
          prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
        );
        recountRef.current = true;
      } else if (recount) {
        recountRef.current = true;
      }
      debouncedFetch();
    };
    (fn as any).cancel = () => debouncedFetch.cancel();
    return fn as ((resetFilters?: boolean, recount?: boolean) => void) & {
      cancel: () => void;
    };
  }, [debouncedFetch]);

  const filterKey = useMemo(() => JSON.stringify(filter ?? {}), [filter]);
  const persistentFilterKey = useMemo(
    () => JSON.stringify(persistentFilter ?? {}),
    [persistentFilter],
  );
  const fieldsKey = useMemo(() => (fields ? fields.join("|") : ""), [fields]);
  const prevFieldsKeyRef = useRef(fieldsKey);
  useEffect(() => {
    if (prevFieldsKeyRef.current !== fieldsKey) {
      prevFieldsKeyRef.current = fieldsKey;
      debouncedFetch();
    }
  }, [fieldsKey, debouncedFetch]);

  useEffect(() => {
    recountRef.current = true;
    setPaginationState((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
    debouncedFetch();
  }, [filterKey, persistentFilterKey, debouncedFetch]);

  useEffect(() => {
    debouncedFetch();
  }, [
    sort.field,
    sort.order,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedFetch,
  ]);

  useEffect(() => {
    loadPageState(setSortState, setFilter, setPaginationState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return {
    data,
    totalCount,
    fetchData,
    setFilter,
    pagination,
    setPagination,
    sort,
    setSort,
    filter,
    inputFilters,
    setInputFilters,
  };
};

export default useDataLoader;
